/**
 * =============================================================================
 * PD METER - MEDIDOR DE DISTÂNCIA NASO-PUPILAR (DNP)
 * =============================================================================
 *
 * Este arquivo contém a integração com o serviço de medição de DNP (Distância
 * Naso-Pupilar) usando a biblioteca GlassesOn/Luna. Foi incorporado como código
 * legado do tema antigo e apenas adaptado para funcionar no novo tema.
 *
 * FUNCIONALIDADE:
 * --------------
 *
 * O medidor DNP permite que os usuários meçam automaticamente a distância entre
 * suas pupilas usando a câmera do dispositivo, eliminando a necessidade de
 * medição manual com régua.
 *
 * INTEGRAÇÃO:
 * ----------
 *
 * - Utiliza a biblioteca GlassesOn (versão 2.31.0) hospedada no Shopify CDN
 * - Configurado com clientId específico da Zerezes
 * - Integra com o sistema de receitas médicas via eventos customizados
 *
 * ARQUITETURA LEGADA:
 * ------------------
 *
 * Este código foi desenvolvido rapidamente para migração do tema antigo e
 * apresenta várias limitações:
 *
 * - Estrutura baseada em propriedades globais do window
 * - Falta de tipagem TypeScript adequada
 * - Ausência de tratamento robusto de erros
 * - Dependência de bibliotecas externas não tipadas
 *
 * LIMITAÇÕES CONHECIDAS:
 * ---------------------
 *
 * - Código legado incorporado sem refatoração adequada
 * - Diversos erros de linting
 * - Falta de separação de responsabilidades
 *
 * FUTURAS MELHORIAS:
 * -----------------
 *
 * - Refatoração completa usaaddo jsdoc para type safety
 * - Implementação de tratamento robusto de erros
 * - Separação de responsabilidades
 * - Melhoria na arquitetura de eventos
 *
 * NOTA: Este código foi desenvolvido com pressa para migração e está sujeito
 * a futuras refatorações para melhorar qualidade e manutenibilidade.
 *
 * =============================================================================
 */

window.pdMeter = {};

/**
 * Função para carregar scripts dinamicamente
 * @param {string} src - URL do script
 * @param {Function} callback - Callback a ser executado após carregamento
 */
window.pdMeter.loadScript = (src, callback) => {
  // Verificar se o script já foi carregado
  const existingScript = document.querySelector(`script[src="${src}"]`);
  if (existingScript) {
    if (callback) callback();
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;

  script.onload = () => {
    if (callback) callback();
  };

  script.onerror = () => {
    console.error('[pdMeter] Erro ao carregar script:', src);
  };

  document.head.appendChild(script);
};

/**
 * Original cdn for pdMeter -> https://web.cdn.glasseson.com/glasseson-3.3.0.js
 * We replaced it to use a version that we're hosting in shopify, since we faced some issues with original cdn version
 */
window.pdMeter.init = () => {
  const scriptUrl =
    'https://cdn.shopify.com/s/files/1/0399/6470/4933/files/glasseson-3.3.0.js?v=1761649606';

  // Verificar se o script já foi carregado
  const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
  if (existingScript) {
    window.pdMeter.setupPdMeter();
    return;
  }

  // Carregar o script do glasseson
  window.pdMeter.loadScript(scriptUrl, () => {
    window.pdMeter.setupPdMeter();
  });
};

window.pdMeter.setupPdMeter = () => {
  // Verificar se a biblioteca glasseson está disponível
  if (typeof window.glasseson === 'undefined') {
    console.error('[pdMeter] Biblioteca glasseson não encontrada');
    return;
  }

  /* identificação da zerezes e dados para configurações */
  const clientId = '3972a2a3-ab6e-489c-9b90-17bd2748cdd1';
  const serverUrl = 'https://api.glasseson.com/prod/';
  const options = {
    profileName: 'web',
    flow: 'pd',
    fullPage: true,
  };

  /* inicialização da biblioteca */
  try {
    window.glasseson
      .init(clientId, serverUrl, options)
      .then(response => {
        // GlassOn iniciado com sucesso
      })
      .catch(error => {
        console.error('[pdMeter] Erro ao inicializar GlassOn:', error);
      });
  } catch (error) {
    console.error('[pdMeter] Erro ao chamar glasseson.init:', error);
    return;
  }

  /* retorno da biblioteca, após obter a DP do usuário */
  try {
    window.glasseson.setResultCallback(result => {
      if (window.glasseson && typeof window.glasseson.close === 'function') {
        window.glasseson.close();
      }

      if (result && result.data && result.data.pd) {
        window.pdMeter.triggerPdMeter(result.data.pd);
      } else if (result && result.pd) {
        window.pdMeter.triggerPdMeter(result.pd);
      } else if (result && typeof result === 'number') {
        window.pdMeter.triggerPdMeter(result);
      } else {
        console.error('[pdMeter] Dados inválidos recebidos:', result);
      }
    });
  } catch (error) {
    console.error('[pdMeter] Erro ao configurar callback:', error);
  }
};

window.pdMeter.triggerPdMeter = pd => {
  // Validar valor de PD
  if (!pd || isNaN(pd) || pd <= 0) {
    console.error('[pdMeter] Valor de PD inválido:', pd);
    return;
  }

  const PdMeterEvent = new CustomEvent('pdmeter:result', {
    detail: {
      pd: parseFloat(pd),
    },
  });

  document.dispatchEvent(PdMeterEvent);
};

window.pdMeter.openPdMeter = () => {
  // Verificar se glasseson está disponível
  if (typeof window.glasseson === 'undefined') {
    console.error('[pdMeter] Biblioteca glasseson não encontrada');
    return false;
  }

  // Verificar se o método open existe
  if (typeof window.glasseson.open !== 'function') {
    console.error('[pdMeter] Método glasseson.open não encontrado');
    return false;
  }

  try {
    const result = window.glasseson.open('pd');

    // Se o resultado é uma Promise, aguardar ela
    if (result && typeof result.then === 'function') {
      result
        .then(() => {
          // Medidor DNP aberto com sucesso
        })
        .catch(error => {
          console.error('[pdMeter] Erro na Promise do glasseson.open:', error);
        });
    }

    return true;
  } catch (error) {
    console.error('[pdMeter] Erro ao abrir medidor DNP:', error);
    return false;
  }
};

window.pdMeter.open = () => window.pdMeter.openPdMeter();

/**
 * Verifica se o medidor DNP está pronto para uso
 * @returns {boolean} true se estiver pronto, false caso contrário
 */
window.pdMeter.isReady = () => {
  const isGlassesonAvailable = typeof window.glasseson !== 'undefined';
  const hasOpenMethod =
    isGlassesonAvailable && typeof window.glasseson.open === 'function';
  const hasInitMethod =
    isGlassesonAvailable && typeof window.glasseson.init === 'function';
  const hasSetResultCallback =
    isGlassesonAvailable &&
    typeof window.glasseson.setResultCallback === 'function';

  return (
    isGlassesonAvailable &&
    hasOpenMethod &&
    hasInitMethod &&
    hasSetResultCallback
  );
};

/**
 * Inicializa o medidor DNP de forma segura
 * @returns {Promise<boolean>} true se inicializado com sucesso, false caso contrário
 */
window.pdMeter.initialize = () => {
  return new Promise(resolve => {
    // Se já estiver pronto, resolver imediatamente
    if (window.pdMeter.isReady()) {
      resolve(true);
      return;
    }

    // Tentar inicializar
    try {
      window.pdMeter.init();

      // Aguardar com múltiplas tentativas
      let attempts = 0;
      const maxAttempts = 10; // 10 tentativas de 500ms = 5 segundos total

      const checkReady = () => {
        attempts++;

        if (window.pdMeter.isReady()) {
          resolve(true);
        } else if (attempts >= maxAttempts) {
          console.warn(
            '[pdMeter] Medidor não ficou pronto após todas as tentativas'
          );
          resolve(false);
        } else {
          setTimeout(checkReady, 500);
        }
      };

      // Começar a verificar após 500ms
      setTimeout(checkReady, 500);
    } catch (error) {
      console.error('[pdMeter] Erro na inicialização:', error);
      resolve(false);
    }
  });
};
